import { Component, computed, effect, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TextInputComponent } from "../../../shared-with-client/text-input/text-input.component";
import { CheckoutMode, ICae, ICartItem, ICreateSaleData, InvoiceStatus, InvoicingService, IPaymentMethod, ISaleStatus } from '../../../core/services/invoicing.service';
import { saleLinesHaveNonZeroUnitPrice } from '../../../core/services/invoicing-cart.util';
import { MerchantsService } from '../../../core/services/merchants.service';
import { StoresService } from '../../../core/services/stores.service';
import { ToolsService } from '../../../core/services/tools.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs';
import { LoaderComponent } from '../../../shared-with-client/loader/loader.component';
import { TabBarComponent } from "../../../shared/tab-bar/tab-bar.component";
import { MerchantCurrencyPipe } from '../../../shared/pipes/merchant-currency.pipe';
import { ToastNotificationsService } from '../../../core/services/toast-notifications.service';
import { MultiCheckButtonsComponent } from '../../../shared/multi-check-buttons/multi-check-buttons.component';
import { SubmitBtnComponent } from "../../../shared-with-client/submit-btn/submit-btn.component";
import { TextareaInputComponent } from '../../../shared-with-client/textarea-input/textarea-input.component';
import { IntlService } from '../../../core/services/intl.service';
import { IconComponent } from '../../../shared-with-client/icon/icon.component';
import { DropdownInputV2Component } from '../../../shared-with-client/dropdown-input-v2/dropdown-input-v2.component';
import { TeyaService } from '../../../core/services/teya.service';

interface ICaeOption extends ICae {
    label: string;
}

@Component({
  selector: 'app-invoice-payment-method',
  imports: [TextInputComponent, TranslatePipe, ReactiveFormsModule, LoaderComponent, TabBarComponent, MerchantCurrencyPipe, MultiCheckButtonsComponent, SubmitBtnComponent, TextareaInputComponent, IconComponent, DropdownInputV2Component],
  templateUrl: './invoice-payment-method.component.html',
  styleUrl: './invoice-payment-method.component.scss'
})
export class InvoicePaymentMethodComponent implements OnInit {
    @ViewChild('paymentMethodsContainer', { static: false }) paymentMethodsContainer!: ElementRef;

    CheckoutMode = CheckoutMode;
    InvoiceStatus = InvoiceStatus;

    private invoicingService = inject(InvoicingService);
    private intlService = inject(IntlService);
    private translateService = inject(TranslateService);
    private toastNotificationsService = inject(ToastNotificationsService);
    private merchantService = inject(MerchantsService);
    private storesService = inject(StoresService);
    private router = inject(Router);
    private fb = inject(FormBuilder);
    private activatedRoute = inject(ActivatedRoute);
    private toolsService = inject(ToolsService);
    private teyaService = inject(TeyaService);

    public currentMerchant = toSignal(this.merchantService.activeMerchant);
    public currentStore = toSignal(this.storesService.getActiveStore());

    public form!: FormGroup;

    public invoicingCart = toSignal(this.invoicingService.invoicingCart$);
    public invoicingCartTotals = toSignal(this.invoicingService.invoicingCartTotals$, { initialValue: {} });
    public selectedPatient = toSignal(this.invoicingService.invoicingPatient$, { initialValue: null });
    public checkoutMode = toSignal(this.invoicingService.checkoutMode$, { initialValue: CheckoutMode.CREATE_FULL_SALE });

    public existingSaleData = signal<any>(null);

    public activeTab = signal<'full' | 'partial' | 'later'>('full');

    public clientEditMode = signal(false);

    public PaymentMethod = IPaymentMethod;
    public ISaleStatus = ISaleStatus;

    public tabOptions: any[] = [];

    public submitAttempted = signal(false);
    public saving = signal(false);
    public loading = signal(false);

    public chargingMethods = this.invoicingService.chargingMethods;

    public teyaConnectionStatus = toSignal(this.teyaService.checkOrFetchConnectionStatus(this.currentStore()?.id));

    public paymentMethods = computed(() => {
        const paymentMethods = this.invoicingService.paymentMethods.filter(m => !m.isOnlinePayment).filter(m => {
            const country = this.intlService.getCurrentCountry();
            if ([IPaymentMethod.MBREF, IPaymentMethod.MBWAY].includes(m.value) && country !== 'pt') {
                return false;
            }
            return true;
        });

        const chargingMethods = this.teyaConnectionStatus()?.active ? this.chargingMethods : [];

        return [
            ...paymentMethods.slice(0, -1),
            ...chargingMethods,
            ...paymentMethods.slice(-1),
        ];
    });

    public maxPaymentAmountCents = signal(0);

    public isInvoicingActive = toSignal(this.storesService.isInvoicingActive());

    public caeOptions = signal<ICaeOption[]>([]);

    isChargingMethodSelected = signal(false);

    // teyaConnectionStatus can resolve after the form is built, so keep salePayments controls in sync
    private syncSalePaymentsControls = effect(() => {
        const methods = this.paymentMethods();
        const salePaymentsGroup = this.form?.get('salePayments') as FormGroup | undefined;

        methods.forEach(method => {
            if (salePaymentsGroup && !salePaymentsGroup.contains(method.value)) {
                salePaymentsGroup.addControl(method.value, this.fb.control(0));
            }
        });
    });

    hasPaymentAmountToCollect(): boolean {
        return (this.maxPaymentAmountCents() || 0) > 0;
    }

    showCaeSelection(): boolean {
        return !!this.currentStore()?.caeSelectionActive
            && !!this.isInvoicingActive()
            && this.canEditClientDetails();
    }

    canEditClientDetails(): boolean {
        return [CheckoutMode.CREATE_FULL_SALE, CheckoutMode.INVOICE_REMAINING].includes(this.checkoutMode());
    }

    ngOnInit(): void {
        const saleId = this.activatedRoute.snapshot?.params['saleId'];

        if (saleId) {
            const checkoutMode = this.activatedRoute.snapshot.data as { invoiceCreationMode?: CheckoutMode };

            // Editing existing sale OR adding payment to sale
            this.invoicingService.checkoutMode$.next(checkoutMode?.invoiceCreationMode || CheckoutMode.ADD_PAYMENT);
            this.loading.set(true);

            const isInvoiceRemaining = checkoutMode?.invoiceCreationMode === CheckoutMode.INVOICE_REMAINING;

            this.invoicingService.getSaleDetails(saleId, this.currentStore().id).subscribe({
                next: (res) => {
                    // A paid sale can still need invoicing (e.g. payment succeeded but the
                    // invoice failed to emit), so only bounce paid sales when adding a payment.
                    if (res.status === ISaleStatus.PAID && !isInvoiceRemaining) {
                        this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/create');
                        return;
                    }
                    this.existingSaleData.set(res);
                    this.invoicingService.setPatientFromSale(res);

                    this.invoicingService.generateExistingSalePreview(res);

                    this.setupForm(res);
                },
                error: (error) => {
                    // If error (sale not found), redirect to sales page
                    this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/sales');
                }
            });

        } else {
            // New sale/payment creation flow
            // Redirect to payment creation if no items in cart
            this.invoicingService.checkoutMode$.next(CheckoutMode.CREATE_FULL_SALE);

            if (!this.invoicingCart()?.saleLines.length || !saleLinesHaveNonZeroUnitPrice(this.invoicingCart()?.saleLines)) {
                this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/create');
                return;
            }

            this.setupForm();
        }

    }

    setupForm(data?: any) {
        // Set client edit mode and max payment amount
        if (data) {
            // existing sale payment mode
            this.clientEditMode.set(false);
            this.maxPaymentAmountCents.set((data?.totalCents - data.paidAmountCents) || 0);
        } else {
            // new sale creation mode
            this.clientEditMode.set(!this.selectedPatient());
            this.maxPaymentAmountCents.set(this.invoicingCartTotals()?.totalCents || 0);
        }

        const hasPositivePaymentAmount = this.maxPaymentAmountCents() > 0;

        this.tabOptions = [];

        if (hasPositivePaymentAmount) {
            this.tabOptions.push({ label: this.translateService.instant('INVOICE_PAYMENT_METHOD.full'), value: 'full' });
            this.tabOptions.push({ label: this.translateService.instant('INVOICE_PAYMENT_METHOD.partial'), value: 'partial' });
            if (this.checkoutMode() === CheckoutMode.CREATE_FULL_SALE || (this.checkoutMode() === CheckoutMode.INVOICE_REMAINING)) {
                this.tabOptions.push({ label: this.translateService.instant('INVOICE_PAYMENT_METHOD.later'), value: 'later' });
            }
        } else {
            this.activeTab.set('full');
        }

        const { clientName, clientVatNumber } = this.invoicingService.resolveInvoiceClientBillingDetails(
            data,
            this.invoicingService.invoicingPatient$.value,
        );

        this.form = this.fb.group({
            clientName: [clientName || ''],
            clientVatNumber: [clientVatNumber || ''],
            cae: [null],
            activePaymentMethods: [[]], // Initialize with an empty array
            partialPaymentAmount: [0],
            salePayments: this.fb.group({}),
            description: ['', Validators.maxLength(200)],
        });

        this.paymentMethods().forEach(method => {
            (this.form.get('salePayments') as FormGroup)?.addControl(method.value, this.fb.control(0));
        });

        this.form.get('activePaymentMethods')?.valueChanges.subscribe(() => {
            this.resetFormValuesAndValidations();

            if (this.form.value.activePaymentMethods?.length) {
                const includesChargingMethod = this.form.value.activePaymentMethods?.some(method => this.chargingMethods?.map(m => m?.value).includes(method));
                this.isChargingMethodSelected.set(includesChargingMethod);
            } else {
                this.isChargingMethodSelected.set(false);
            }

            // Smooth scroll to the payment div
            setTimeout(() => {
                if (this.paymentMethodsContainer) {
                    this.paymentMethodsContainer.nativeElement.scrollIntoView({ behavior: 'smooth' });
                }
            }, 20);
        });

        this.form.get('partialPaymentAmount')?.valueChanges
            .pipe(distinctUntilChanged())
            .subscribe((value) => {
                setTimeout(() => {
                    this.resetFormValuesAndValidations();
                }, 20);
            });

        this.resetFormValuesAndValidations();

        this.loadCaes();

        this.loading.set(false);
    }

    private loadCaes() {
        if (!this.showCaeSelection()) {
            return;
        }

        this.invoicingService.getCaes(this.currentStore().id).subscribe({
            next: (res) => {
                const options = (res?.caes || []).map((cae) => ({
                    ...cae,
                    label: cae.description ? `${cae.code} - ${cae.description}` : cae.code,
                }));
                this.caeOptions.set(options);
                if (options.length) {
                    this.form.get('cae')?.setValue(options[0]);
                }
            },
            // A failed lookup must never block invoicing: hide the dropdown instead.
            error: () => this.caeOptions.set([]),
        });
    }

    resetFormValuesAndValidations() {
        let totalCents = 0;
        if (this.activeTab() === 'full') {
            totalCents = this.maxPaymentAmountCents() || 0;
            this.setSalePaymentDefaultValues(totalCents);
        }

        if (this.activeTab() === 'partial') {
            totalCents = this.form.value.partialPaymentAmount ? Math.round((this.form.value.partialPaymentAmount * 100)) : 0;
            this.setSalePaymentDefaultValues(totalCents);
        }

        if (this.activeTab() === 'later') {
            this.form.get('activePaymentMethods')?.patchValue([], { emitEvent: false });
        }

        this.setPaymentMethodValidators();
        this.setPartialPaymentValidators();
        this.setSalePaymentValidators(totalCents);

        this.form.updateValueAndValidity();

    }

    setPaymentMethodValidators() {
        const totalCents = this.maxPaymentAmountCents() || 0;
        if (this.activeTab() === 'later' || totalCents === 0) {
            this.form.get('activePaymentMethods')?.setValidators(null);
        } else {
            this.form.get('activePaymentMethods')?.setValidators([Validators.required, Validators.minLength(1)]);
        }
        this.form.get('activePaymentMethods')?.updateValueAndValidity({ emitEvent: false });
    }

    setPartialPaymentValidators() {
        const maxValue = this.maxPaymentAmountCents() || 0
        if (this.activeTab() === 'partial') {
            this.form.get('partialPaymentAmount')?.setValidators([Validators.required, this.toolsService.positiveNumberValidator(), Validators.max(maxValue/100)]);
        } else {
            this.form.get('partialPaymentAmount')?.setValidators(null);
            this.form.get('partialPaymentAmount')?.patchValue(0);
        }
        this.form.get('partialPaymentAmount')?.updateValueAndValidity();
    }

    setSalePaymentDefaultValues(totalCents: number) {
        setTimeout(() => {
            const activeMethods = this.form.value.activePaymentMethods;
            if (!activeMethods || activeMethods.length === 0) return;

            const salePaymentsGroup = this.form.get('salePayments') as FormGroup;

            // Calculate sum of all but last selected payment method
            let sumOtherMethods = 0;
            for (let i = 0; i < activeMethods.length - 1; i++) {
                const method = activeMethods[i];
                const value = Number(salePaymentsGroup.get(method)?.value) || 0;
                sumOtherMethods += Math.round(value * 100);
            }

            // Remaining amount for the last method
            const remainingCents = totalCents - sumOtherMethods;
            const lastMethod = activeMethods[activeMethods.length - 1];

            // Set the last method's value in euros (cents / 100), but not negative
            salePaymentsGroup.get(lastMethod)?.patchValue(Math.max(remainingCents / 100, 0));
        });
    }

    setSalePaymentValidators(totalCents?: number) {
        if (totalCents === 0) {
            Object.keys((this.form.get('salePayments') as FormGroup).controls).forEach(key => {
                (this.form.get('salePayments') as FormGroup).get(key)?.setValidators(null);
                (this.form.get('salePayments') as FormGroup).get(key)?.patchValue(0, { emitEvent: false });
                (this.form.get('salePayments') as FormGroup).get(key)?.updateValueAndValidity({ emitEvent: false });
            });
            (this.form.get('salePayments') as FormGroup).setValidators(null);
            (this.form.get('salePayments') as FormGroup).updateValueAndValidity({ emitEvent: false });
            return;
        }

        Object.keys((this.form.get('salePayments') as FormGroup).controls).forEach(key => {
            if (this.form.value.activePaymentMethods.includes(key)) {
                (this.form.get('salePayments') as FormGroup).get(key)?.setValidators([Validators.required, this.toolsService.positiveNumberValidator()]);
            } else {
                (this.form.get('salePayments') as FormGroup).get(key)?.setValidators(null);
                (this.form.get('salePayments') as FormGroup).get(key)?.updateValueAndValidity();
                (this.form.get('salePayments') as FormGroup).get(key)?.patchValue(0);
            }
        });
        if (totalCents) {
            // add validator to check if the sum of all payment methods is equal to totalCents
            (this.form.get('salePayments') as FormGroup).setValidators((control) => {
                const group = control as FormGroup;
                const sumCents = Object.keys(group.controls).reduce((acc, key) => {
                    const value = this.form.value.activePaymentMethods.includes(key) ? (Number(group.get(key)?.value) || 0) : 0;
                    return acc + Math.round(value * 100);
                }, 0);
                return sumCents === totalCents ? null : { sumMismatch: true };
            });
            (this.form.get('salePayments') as FormGroup).updateValueAndValidity();
        } else {
            (this.form.get('salePayments') as FormGroup).setValidators(null);
            (this.form.get('salePayments') as FormGroup).updateValueAndValidity();
        }
    }

    onSubmit({createDraft = false, updateDraft = false} = {}) {
        if (this.saving()) {
            return;
        }

        this.submitAttempted.set(true);

        const finalizeDraft = this.existingSaleData()?.status === ISaleStatus.DRAFT && !createDraft && !updateDraft;

        if (!this.form.valid) {
            return;
        }
        this.saving.set(true);
        const shouldCreateSalePayments = (this.maxPaymentAmountCents() || 0) > 0;
        const salePayments = (shouldCreateSalePayments ? this.form.value.activePaymentMethods : []).map((method: string) => {
            return {
                paymentMethod: method,
                amountCents: Math.round((this.form.value.salePayments[method] || 0) * 100),
                currency: this.currentMerchant()?.currency || undefined,
            };
        });

        const saleLines: ICartItem[] = this.invoicingService.mapSaleLineToSave(this.invoicingCart()?.saleLines);

        const payload: ICreateSaleData = {
            storeId: this.currentStore().id,
            merchantId: this.currentMerchant().id,
            currency: this.currentMerchant()?.currency,
            merchantClientId: this.selectedPatient()?.id || null,

            clientName: this.form.value.clientName || null,
            clientVatNumber: String(this.form.value.clientVatNumber) || null,

            ...(this.showCaeSelection() ? { cae: this.form.value.cae?.code || null } : {}),

            description: this.form.value.description || '',

            payLater: this.activeTab() === 'later',
            partialPayment: this.activeTab() === 'partial',

            discountPercentage: Number(this.invoicingCart()?.discountPercentage) || 0,
            discountAmountCents: Number(this.invoicingCart()?.discountAmountCents) || 0,

            saleLines,
            salePayments
        };

        let request;
        if (createDraft) {
            request = this.invoicingService.createDraft(payload);
        } else if (updateDraft) {
            request = this.invoicingService.updateDraft(this.existingSaleData()?.id, payload);
        } else if (finalizeDraft) {
            request = this.invoicingService.finalizeDraft(this.existingSaleData()?.id, payload);
        } else {
            if (this.checkoutMode() === CheckoutMode.CREATE_FULL_SALE) {
                request = this.invoicingService.createSale(payload)
            } else if (this.checkoutMode() === CheckoutMode.INVOICE_REMAINING) {
                request = this.invoicingService.addInvoiceToExistingSale(this.existingSaleData()?.id, payload);
            } else {
                // CheckoutMode.ADD_PAYMENT, CheckoutMode.EDIT_SALE
                request = this.invoicingService.createPaymentForExistingSale(this.existingSaleData()?.id, payload);
            }
        }

        request?.subscribe({
            next: (res) => {
                if (createDraft || updateDraft) {
                    // Drafts are not completed sales: skip the Teya / success steps
                    this.saving.set(false);
                    this.toastNotificationsService.showToast(this.translateService.instant('GENERAL.savedSuccessfully'), 'success');
                    this.invoicingService.clearCart();
                    this.invoicingService.setPatient(null);
                    this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/sales');
                    return;
                }

                this.invoicingService.completeInvoiceData$.next(res);

                const teyaUnchargedAmount = salePayments?.find(payment => payment.paymentMethod === IPaymentMethod.TEYA)?.amountCents || 0;

                this.invoicingService.unchargedTeyaAmount$.next(teyaUnchargedAmount);

                this.goToNextStep();

                this.invoicingService.generateExistingSalePreview(res);
            },
            error: (error) => {
                this.toastNotificationsService.showToast(error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'), 'error');
                this.saving.set(false);
            }
        });
    }

    onPaymentTabSelected(tab: string) {
        this.resetFormValuesAndValidations();
    }

    goToNextStep() {
        if (this.isChargingMethodSelected()) {
            this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/teya');
        } else if (this.checkoutMode() === CheckoutMode.CREATE_FULL_SALE) {
            this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/success');
        } else {
            this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/payment-success');
        }
    }

    saveDraft() {
        this.onSubmit({createDraft: true});
    }

    updateDraft() {
        this.onSubmit({updateDraft: true});
    }

    goBack() {
        if (this.saving()) {
            return;
        }

        this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/create');
    }

}
